import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useState, useEffect, ChangeEvent } from "react";
import api from "../../../../services/api";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import toastOptions from "../../../../utils/toastOptions";
import { authenticationService } from "../../../../services/auth.service";
import MemberFile from "../../../../models/MemberFile";
import { CPFIsValid, cpfMask, filesAllowed, handleFocus, phoneMask } from "../../../../utils/masks";
import { ModalAddEdit } from "../../../../containers";
import { Gender } from "../../../../models/enums/Gender";
import IPersonDTO from "../../../../models/IPersonDTO";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";

interface Params {
  id?: number;
  add: boolean;
  afterHandleSubmit: any;
  afterCloseModal: any;
}

export default function ProtocolAddEdit(props: Params) {
  const { id, add, afterHandleSubmit, afterCloseModal } = props;

  const [modalOpen, setModelOpen] = useState<boolean>(false);
  const [isReadOnly, setReadOnly] = useState<boolean>(true);
  const [doc, setDoc] = useState<File | string>();
  const [person, setPerson] = useState<IPersonDTO>();

  const schema = yup.object().shape({
    name: yup
      .string()
      .required("Nome obrigatório")
      .max(100, (val) => `Máximo ${val.max} caracteres`)
      .min(3, (val) => `Mínimo ${val.min} caracteres`),
    gender: yup.string().required("Gênero obrigatório"),
    document: yup
      .string()
      .required("CPF obrigatório")
      .test("register-validate", "CPF Inválido", (value) => CPFIsValid(value)),
    phone: yup.string().required(`Telefone é obrigatório`),
    email: yup.string().required(`E-mail é obrigatório`).max(100, (val) => `Máximo ${val.max} caracteres`),
    note: yup.string().required(`Descrição é obrigatória`)
  });

  const { register, handleSubmit, reset, errors, formState, clearErrors, setValue } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    reset();
    clearErrors();
    setReadOnly(authenticationService.isReadOnly());
    setModelOpen(add);
    setDoc(undefined);
    if (id) {
      handleEdit(id);
    }
  }, [id, add]);

  const closeModal = () => {
    handleCloseModal();
    afterCloseModal();
  };

  const handleCloseModal = () => {
    setModelOpen(false);
    reset();
    afterCloseModal();
  };

  const onSubmit = async (data: MemberFile) => {
    try {      
      const formData = new FormData();

      data.id = person?.id;
      // data.idFile = person?.file?.id ?? 0;
      formData.append("data", JSON.stringify(data));

      if (doc) {
        formData.append("doc", doc);
      }

      if (!person?.id) {
        await api.post("protocols", formData);
        toast.success("Protocolo cadastrado com sucesso!", toastOptions);
      } else {
        await api.put("protocols", formData);
        toast.success("Protocolo atualizado com sucesso!", toastOptions);
      }
      handleCloseModal();
      afterHandleSubmit();
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    }
  };

  const handleEdit = async (id: number) => {
    try {
      const { data } = await api.get(`protocols/${id}`);
      // setFile(data.item);
      setDoc(data.item.file);
      setModelOpen(true);
    } catch (e: any) {
      if (e.messages) {
        e.messages.forEach((m: string) => {
          toast.warning(m, toastOptions);
        });
      }
    }
  };

  const handleSelectDoc = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    const img = event.target.files[0];

    if (!filesAllowed(img)) {
      toast.warn("Permitido apenas imagens ou PDF", toastOptions);
      return;
    }

    setDoc(img);
  };

  return (
    <ModalAddEdit<MemberFile>
      title="Novo Protocolo"
      isOpen={modalOpen}
      closeModal={closeModal}
      handleCloseModal={handleCloseModal}
      children={
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return handleSubmit<MemberFile>(onSubmit)(e);
          }}
        >  
          <div className="uk-modal-body">
            <h5 className="uk-text-small uk-margin-remove-top uk-text-primary">
              <FontAwesomeIcon icon={faExclamationCircle} className="uk-margin-small-right" />
              A pessoa que entregou o protocolo será notificada via e-mail
            </h5>
            <div className="uk-grid-small" uk-grid="true">
              <div className="uk-width-3-4@s">
                <label className="uk-form-label" htmlFor="name">Entregue por (Nome da Pessoa)</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Nome"
                  className="uk-input uk-form-small"
                  defaultValue={person?.name}
                  style={{ textTransform: "uppercase" }}
                  autoComplete={"off"}
                  onFocus={handleFocus}
                  ref={register}
                />
                <small className="uk-text-danger">{errors.name?.message}</small>
              </div>
              <div className="uk-width-1-4@s">
                <label className="uk-form-label" htmlFor="gender">Sexo</label>
                <div
                  uk-form-custom="target: > * > span:first-child"
                  className="uk-width-1"
                >
                  <select
                    className="uk-form-small"
                    id="gender"
                    name="gender"
                    defaultValue={person?.gender}
                    ref={register}
                  >
                    <option value="" key={`gender0`}>Selecione...</option>
                    <option value={Gender.FEMALE} key={`gender1`}>Feminino</option>
                    <option value={Gender.MALE} key={`gender2`}>Masculino</option>
                  </select>
                  <button
                    className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                    style={{ textTransform: "none" }}
                    type="button"
                    tabIndex={-1}
                  >
                    <span></span>
                    <span uk-icon="icon: chevron-down"></span>
                  </button>
                </div>
                <small className="uk-text-danger">{errors.gender?.message}</small>
              </div>
              <div className="uk-width-1-4@s">
                <label className="uk-form-label" htmlFor="document">CPF</label>
                <input
                  type="text"
                  id="document"
                  name="document"
                  placeholder="999.999.999-99"
                  className="uk-input uk-form-small"
                  defaultValue={cpfMask(person?.document ?? '')}
                  autoComplete={"off"}
                  ref={register}
                  onFocus={handleFocus}
                  onChange={(e) => {
                    e.target.value = cpfMask(e.target.value);
                  }}
                />
                <small className="uk-text-danger">
                  {errors.document?.message}
                </small>
              </div>
              <div className="uk-width-1-2@s">
                <label className="uk-form-label" htmlFor="email">E-mail</label>
                <input
                  type="text"
                  id="email"
                  name="email"
                  placeholder="email@email.com"
                  className="uk-input uk-form-small"
                  defaultValue={person?.email}
                  autoComplete={"off"}
                  ref={register}
                />
                <small className="uk-text-danger">{errors.email?.message}</small>
              </div>
              <div className="uk-width-1-4@s">
                <label className="uk-form-label" htmlFor="phone">Celular</label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  placeholder="(99) 99999-9999"
                  className="uk-input uk-form-small"
                  defaultValue={phoneMask(person?.phone ?? "")}
                  ref={register}
                  autoComplete={"off"}
                  onChange={(e) => {
                    e.target.value = phoneMask(e.target.value);
                  }}
                />
                <small className="uk-text-danger">{errors.phone?.message}</small>
              </div>                           
              <div className="uk-width-1">
                <label className="uk-form-label" htmlFor="note">Descrição</label>
                <textarea
                  id="note"
                  name="note"
                  className="uk-textarea uk-form-small uk-text-uppercase"
                  rows={4}
                  style={{ resize: "none" }}
                  defaultValue={person?.note}
                  autoComplete={"off"}
                  ref={register}
                  placeholder="Descreva os documentos entregues ou recebidos"
                />
                <small className="uk-text-danger">{errors.note?.message}</small>
              </div>
              <div className="uk-width-1@s uk-flex uk-flex-center uk-flex-bottom">
                <div className="uk-flex uk-flex-column uk-flex-middle">
                  <span uk-icon="icon: file-text; ratio: 4"></span>
                  {!doc &&  <label className="uk-form-label" htmlFor="note">{!doc && "Anexe um documento"}</label>}
                  {doc && <label>{(doc as File).name}</label>}
                  <div className="js-upload" uk-form-custom="true">
                    <input
                      type="file"
                      id="file-input-photo"
                      multiple={false}
                      onChange={handleSelectDoc}
                    />
                    <button
                      className="uk-button uk-button-danger uk-button-small"
                      type="button"
                      tabIndex={-1}
                      disabled={formState.isSubmitting}
                    >
                      {doc ? "Documento" : "Selecione..."}
                    </button>
                  </div>                  
                </div>
                {doc &&
                  <button
                    onClick={(ev) => { 
                      ev.preventDefault(); 
                      setDoc(undefined);
                    }}
                    className="uk-button uk-button-secondary uk-button-small cursor-pointer"
                    type="button"
                    disabled={formState.isSubmitting}
                  >
                    <FontAwesomeIcon icon={faClose} uk-tooltip="title: Remover Documento" />
                  </button>
                }
              </div>
            </div>
          </div>
          <div className="uk-modal-footer">
            <div
              className="uk-grid-small uk-child-width-1-4@s uk-flex-center uk-text-center"
              uk-grid="true"
            >
              <button
                className="uk-button uk-button-default uk-button-small uk-modal-close uk-margin-left"
                onClick={handleCloseModal}
                type="button"
              >
                Cancelar
              </button>
              <button
                disabled={formState.isSubmitting || isReadOnly}
                className="uk-button uk-button-primary uk-button-small uk-margin-left"
                type="submit"
              >
                {formState.isSubmitting && <div uk-spinner="true; ratio:0.5"></div>}
                Salvar
              </button>
            </div>
          </div>
        </form>
      }
    />
  );
}
